import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Kbd } from '../ui/kit.tsx';
import { companionApi } from './api.ts';
import type { CompanionStatus } from './types.ts';
import { FIELD_LABELS, changedWords, wandKeyAction, type WandEntry } from './wand.ts';
import './wand.css';

let statusPromise: Promise<CompanionStatus> | null = null;

/** Whether the model is configured (asked once per page load; the wand is disabled with the reason if not). */
export function useWandStatus(): CompanionStatus | null {
  const [status, setStatus] = useState<CompanionStatus | null>(null);
  useEffect(() => {
    let live = true;
    statusPromise ??= companionApi.status().catch(() => ({ configured: false, provider: null, model: null, reason: 'The magic wand is not available right now.', maxRows: 0, undoSeconds: 0 }));
    void statusPromise.then((value) => {
      if (live) setStatus(value);
    });
    return () => {
      live = false;
    };
  }, []);
  return status;
}

function WandIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M2 14 L10 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M11.5 1.5 L12.2 3.3 L14 4 L12.2 4.7 L11.5 6.5 L10.8 4.7 L9 4 L10.8 3.3 Z" fill="currentColor" />
      <circle cx="5" cy="3" r="0.9" fill="currentColor" />
      <circle cx="14" cy="9" r="0.9" fill="currentColor" />
    </svg>
  );
}

/** The small wand next to a field. Not rendered at all for people who may not edit the field. */
export function WandButton({ label, status, busy, onClick }: { label: string; status: CompanionStatus | null; busy: boolean; onClick: () => void }) {
  if (!status) return null;
  const disabled = !status.configured || busy;
  const title = !status.configured ? (status.reason ?? 'The magic wand is not configured.') : busy ? 'Thinking…' : `Magic wand: ${label}`;
  return (
    <button type="button" className={`wand-btn ${busy ? 'wand-busy' : ''}`} disabled={disabled} title={title} aria-label={title} onClick={onClick} data-testid="wand">
      <WandIcon />
    </button>
  );
}

/**
 * A proposal inline under its field: proposed value, the current one struck through, a one-line reason and any
 * warnings. Enter accepts (the value goes into the draft; nothing is saved), Esc rejects, r asks again (with a note).
 */
export function WandProposal({ entry, siteName, onAccept, onReject, onRetry }: { entry: WandEntry; siteName?: string; onAccept: () => void; onReject: () => void; onRetry: (note: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!entry.accepted) box.current?.focus();
  }, [entry.key, entry.changesetRef, entry.accepted]);

  if (entry.accepted) {
    return (
      <p className="wand-accepted" role="status">
        Wand {FIELD_LABELS[entry.field]} accepted; saved when you save{siteName ? ` ${siteName}` : ''}.
      </p>
    );
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (noting) return;
    const action = wandKeyAction(event);
    if (!action || (action === 'accept' && entry.status !== 'proposed')) return;
    // Esc must not also close the drawer.
    event.preventDefault();
    event.stopPropagation();
    if (action === 'accept') onAccept();
    else if (action === 'reject') onReject();
    else setNoting(true);
  };
  const warnings = entry.issues.filter((issue) => issue.level !== 'info' || entry.status !== 'proposed');

  return (
    <div className={`wand-proposal wand-${entry.status}`} tabIndex={0} ref={box} onKeyDown={onKeyDown} role="group" aria-label={`Magic wand proposal for the ${FIELD_LABELS[entry.field]}`} data-testid="wand-proposal">
      {entry.status === 'proposed' && entry.proposed !== null ? (
        <>
          <div className="wand-new">
            {changedWords(entry.current, entry.proposed).map((part, i) => (part.changed ? <mark key={i}>{part.text}</mark> : <span key={i}>{part.text}</span>))}
          </div>
          {entry.current ? <del className="wand-old">{entry.current}</del> : null}
          <div className="wand-reason">{entry.reason}</div>
        </>
      ) : (
        <div className="wand-reason">{entry.status === 'unchanged' ? entry.reason : `No proposal. ${entry.reason}`}</div>
      )}
      {warnings.length ? (
        <ul className="wand-issues">
          {warnings.map((issue, i) => (
            <li key={i} className={`wand-issue-${issue.level}`}>
              {issue.message}
            </li>
          ))}
        </ul>
      ) : null}
      {noting ? (
        <form
          className="wand-note"
          onSubmit={(event) => {
            event.preventDefault();
            setNoting(false);
            onRetry(note.trim());
          }}
        >
          <input
            autoFocus
            value={note}
            maxLength={500}
            placeholder="What should be different? (optional, Enter sends)"
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setNoting(false);
                box.current?.focus();
              }
            }}
          />
        </form>
      ) : (
        <div className="wand-actions">
          {entry.status === 'proposed' ? (
            <button type="button" className="btn btn-small btn-primary" onClick={onAccept}>
              Accept <Kbd>Enter</Kbd>
            </button>
          ) : null}
          <button type="button" className="btn btn-small btn-ghost" onClick={onReject}>
            {entry.status === 'proposed' ? 'Reject' : 'Dismiss'} <Kbd>Esc</Kbd>
          </button>
          <button type="button" className="btn btn-small btn-ghost" onClick={() => setNoting(true)}>
            Try again <Kbd>r</Kbd>
          </button>
        </div>
      )}
    </div>
  );
}

/** "Undo last save" with the companion's undo window, shown after a wand-backed save. */
export function UndoLastSave({ label, undoUntil, onUndo }: { label: string; undoUntil: string; onUndo: () => void }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const left = Math.max(0, Date.parse(undoUntil) - now);
  if (left <= 0) return null;
  return (
    <button type="button" className="btn btn-small wand-undo" onClick={onUndo} title={`Undo the last saved change (${label})`} data-testid="wand-undo">
      Undo last save · {label} · {Math.floor(left / 60000)}:{String(Math.floor((left % 60000) / 1000)).padStart(2, '0')}
    </button>
  );
}
